import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@mui/material';

import PaddingContainer from '@/components/PaddingContainer/PaddingContainer';
import Stepper from '@/components/Search/components/SearchHeader/Stepper/Stepper';
import { SearchFiltersType } from '@/components/Search/types';
import SearchBar from '@/Organisms/SearchBar';
import {
  HighlightReturn,
  orderLocationData,
  orderSubjects,
} from '@/Organisms/SearchBar/utils';
import { defaultSearchSubjects } from '@/shared/defaultSearchSubjects';
import { useAbTestSearch } from '@/shared/Hooks/abTests/useAbTestSearch';
import { Subject } from '@/types/subject';

import messages from './messages';

import useStyles from './styles';

type Props = Pick<
  SearchFiltersType,
  'subject' | 'setSubject' | 'address' | 'setAddress'
> & {
  subjects: Subject[];
};

const SearchHeader: FC<Props> = ({
  subject,
  setSubject,
  address,
  setAddress,
  subjects,
}) => {
  const [sortedSubjects, setSortedSubjects] = useState<HighlightReturn[][]>();
  useEffect(() => {
    setSortedSubjects(defaultSearchSubjects);
  }, [subjects]);
  const {
    isAbTestSearchLoaded,
    isAbTestSearchNoStepVariant,
    isAbTestSearchNoStepNoDescriptionVariant,
    isAbTestSearchNoStepNoDescriptionNoTitleVariant,
  } = useAbTestSearch();
  const isStepperHidden =
    isAbTestSearchNoStepVariant ||
    isAbTestSearchNoStepNoDescriptionVariant ||
    isAbTestSearchNoStepNoDescriptionNoTitleVariant;
  const { classes } = useStyles({
    isStepperHidden,
    isAbTestSearchNoStepNoDescriptionNoTitleVariant,
  });

  return (
    <div
      className={classes.root}
      style={{ opacity: isAbTestSearchLoaded ? 1 : 0, transition: '0.3s' }}
    >
      <PaddingContainer withMaxWidth>
        <div className={classes.titleContainer}>
          {!isAbTestSearchNoStepNoDescriptionNoTitleVariant && (
            <Typography variant="titleMd" component="h1">
              <FormattedMessage {...messages.title} />
            </Typography>
          )}
          {isAbTestSearchNoStepNoDescriptionVariant ||
          isAbTestSearchNoStepNoDescriptionNoTitleVariant ? null : (
            <Typography
              variant="bodyLg"
              component="h2"
              className={classes.subtitle}
            >
              <FormattedMessage {...messages.subtitle} />
            </Typography>
          )}
        </div>
      </PaddingContainer>

      <PaddingContainer withMaxWidth>
        <div className={classes.searchBarContainer}>
          <SearchBar
            sortedSubjects={sortedSubjects}
            defaultSubject={subject}
            defaultAddress={address?.formattedAddress}
            subjectDatas={subjects}
            onOptionLocationChange={orderLocationData}
            onOptionSubjectChange={(data: Subject[], input?: string) => {
              setSubject(input ?? '');
              return orderSubjects(data, input);
            }}
            onGoogleInputChange={setAddress}
            onSubjectChange={setSubject}
          />
          {!isStepperHidden && <Stepper />}
        </div>
      </PaddingContainer>
    </div>
  );
};

export default SearchHeader;
